import type { ReactNode } from "react";
import { cn } from "@/utils";
import { FieldShell } from "./FieldShell";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export interface SelectOption {
  value: string;
  label: ReactNode;
}

interface SelectFieldProps {
  label?: ReactNode;
  value: string;
  onValueChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  required?: boolean;
  error?: string | null;
  hint?: ReactNode;
  disabled?: boolean;
  id?: string;
  triggerClassName?: string;
  containerClassName?: string;
}

/**
 * Glass-styled Radix select. Self-contained: surfaces and electric focus ring
 * are applied directly to the trigger so it matches the rest of the form set
 * without depending on a `.glass-form` wrapper.
 */
export function SelectField({
  label,
  value,
  onValueChange,
  options,
  placeholder,
  required,
  error,
  hint,
  disabled,
  id,
  triggerClassName,
  containerClassName,
}: SelectFieldProps) {
  return (
    <FieldShell
      id={id}
      label={label}
      required={required}
      error={error}
      hint={hint}
      className={containerClassName}
    >
      <Select value={value} onValueChange={onValueChange} disabled={disabled}>
        <SelectTrigger
          id={id}
          className={cn(
            "h-11 rounded-xl border text-sm transition-shadow",
            "bg-[color:var(--landing-surface)] border-[color:var(--landing-glass-border)]",
            "text-[color:var(--landing-text)]",
            "focus:ring-2 focus:ring-[var(--acc2)]/40 focus:border-[var(--acc2)]/50 focus:ring-offset-0",
            error && "border-destructive/60",
            triggerClassName,
          )}
        >
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </FieldShell>
  );
}
